import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Button } from "@/components/ui/button";
import {
    LayoutDashboard,
    ShoppingBag,
    Tags,
    FileText,
    Settings,
    LogOut,
    MessageSquare,
    Mail,
    Menu,
    X,
} from "lucide-react";

const nav = [
    { path: "/admin/dashboard", label: "Tableau de bord", icon: LayoutDashboard },
    { path: "/admin/products", label: "Produits", icon: ShoppingBag },
    { path: "/admin/categories", label: "Catégories", icon: Tags },
    { path: "/admin/content", label: "Contenu", icon: FileText },
    { path: "/admin/quote-requests", label: "Devis", icon: MessageSquare },
    { path: "/admin/contacts", label: "Contacts", icon: Mail },
    { path: "/admin/settings", label: "Paramètres", icon: Settings },
];

const AdminLayout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { user, logout } = useAdminAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const closeSidebar = () => setSidebarOpen(false);

    const NavLinks = () => (
        <>
            {nav.map((item) => {
                const active = location.pathname.startsWith(item.path);
                const Icon = item.icon;
                return (
                    <Link
                        key={item.path}
                        to={item.path}
                        onClick={closeSidebar}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                            active
                                ? "bg-[#0a84ff]/10 text-[#0a84ff]"
                                : "text-neutral-600 hover:bg-neutral-100"
                        }`}
                    >
                        <Icon className="w-4 h-4" />
                        {item.label}
                    </Link>
                );
            })}
        </>
    );

    return (
        <div className="min-h-screen bg-neutral-50 flex">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/40 z-40 md:hidden"
                    onClick={closeSidebar}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed md:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-neutral-200 flex flex-col
                transform transition-transform duration-200 ease-in-out
                ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
                md:translate-x-0`}
            >
                <div className="p-6 border-b border-neutral-100">
                    <div className="flex items-center justify-between">
                        <Link to="/admin/dashboard" className="flex items-center gap-2.5" onClick={closeSidebar}>
                            <div className="w-8 h-8 rounded-lg bg-[#1C1C1E] flex items-center justify-center">
                                <span className="text-white font-display font-bold text-sm">D</span>
                            </div>
                            <span className="font-display text-lg font-semibold tracking-tight">
                                Dentiprime
                            </span>
                        </Link>
                        <button
                            type="button"
                            onClick={closeSidebar}
                            className="md:hidden p-2 text-neutral-500 hover:text-neutral-900"
                            aria-label="Fermer le menu"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="mt-4 text-xs text-neutral-500">
                        Connecté en tant que <span className="font-medium text-neutral-900">{user?.username}</span>
                    </div>
                </div>

                <nav className="flex-1 p-4 space-y-1">
                    <NavLinks />
                </nav>

                <div className="p-4 border-t border-neutral-100">
                    <Button
                        variant="ghost"
                        className="w-full justify-start text-neutral-600 hover:text-red-600"
                        onClick={() => {
                            logout();
                            navigate("/admin/login");
                        }}
                    >
                        <LogOut className="w-4 h-4 mr-2" />
                        Déconnexion
                    </Button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 overflow-auto min-w-0">
                {/* Mobile header */}
                <div className="md:hidden sticky top-0 z-30 bg-white border-b border-neutral-200 px-4 py-3 flex items-center justify-between">
                    <span className="font-display font-semibold text-neutral-900">Dentiprime Admin</span>
                    <button
                        type="button"
                        onClick={() => setSidebarOpen(true)}
                        className="p-2 text-neutral-600 hover:text-neutral-900"
                        aria-label="Ouvrir le menu"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                </div>
                <div className="max-w-6xl mx-auto p-4 md:p-8">{children}</div>
            </main>
        </div>
    );
};

export default AdminLayout;
